import React from 'react';
import { CopyToClipboard } from '@clockout-dev/design-elements';
import EnrollmentChip from './EnrollmentChip';
import PayrollSourcesPopover from './PayrollSourcesPopover';
import { USER_ID_HEAD, USER_ID_TAIL, USER_HEADER_COPY } from '../user.constants';
import styles from './styles/user-header.module.scss';

/**
 * The meta line under the member's name (Figma UserHeader, KAN-738):
 * Company · Pay type (and frequency) · Enrollment · User ID + copy.
 *
 * With two or more sources the company becomes the "+N" popover trigger and
 * the pay-type slot becomes "N payroll sources" — one member, several pay
 * types, so no single one is asserted. Each segment is optional and the
 * dividers come from CSS, so a missing piece never leaves a dangling bar.
 *
 * @param {Object} props
 * @param {string | null} props.userId The member's `client_id`; shown and copied in full.
 *   Null hides the ID segment.
 * @param {string | null} [props.primaryEmployer]
 * @param {string | null} [props.payTypeLine] Final text, or null to hide it.
 * @param {Array<Object>} [props.payrolls] Popover rows, as DropdownMenu items.
 * @param {number} [props.sourceCount]
 * @param {string | null} [props.sourcesLabel] "2 payroll sources" for a multi-source member.
 * @param {string} [props.sourcesTitle] The popover caption.
 * @param {Object} [props.enrollmentChip] From `buildEnrollmentChip`.
 * @param {boolean} [props.payrollSourcesOpen]
 * @param {(next: boolean, info?: Object) => void} [props.onPayrollSourcesOpenChange]
 * @param {(id: string) => void} [props.onCopyUserId]
 * @param {(error: Error) => void} [props.onCopyUserIdError]
 */
function UserHeaderMeta({
    userId,
    primaryEmployer,
    payTypeLine = null,
    payrolls = [],
    sourceCount = 0,
    sourcesLabel = null,
    sourcesTitle = '',
    enrollmentChip = null,
    payrollSourcesOpen = false,
    onPayrollSourcesOpenChange,
    onCopyUserId,
    onCopyUserIdError,
}) {
    const isMultiSource = sourceCount > 1;

    return (
        <div className={styles.meta} data-testid="user-header-meta">
            {isMultiSource ? (
                <span className={styles.metaItem}>
                    <PayrollSourcesPopover
                        employer={primaryEmployer}
                        title={sourcesTitle}
                        items={payrolls}
                        count={sourceCount}
                        open={payrollSourcesOpen}
                        onOpenChange={onPayrollSourcesOpenChange}
                    />
                </span>
            ) : primaryEmployer ? (
                <span className={styles.metaItem} title={primaryEmployer}>
                    <span className={styles.metaText}>{primaryEmployer}</span>
                </span>
            ) : null}

            {isMultiSource ? (
                <span className={styles.metaItem}>{sourcesLabel}</span>
            ) : payTypeLine ? (
                <span className={styles.metaItem} data-testid="user-header-pay-type">
                    <span className={styles.metaText}>{payTypeLine}</span>
                </span>
            ) : null}

            <EnrollmentChip chip={enrollmentChip} className={styles.metaItem} />

            {userId ? (
                <span className={`${styles.metaItem} ${styles.userId}`}>
                    <span>{USER_HEADER_COPY.USER_ID_PREFIX}</span>
                    <CopyToClipboard
                        value={userId}
                        label={USER_HEADER_COPY.USER_ID_LABEL}
                        head={USER_ID_HEAD}
                        tail={USER_ID_TAIL}
                        onCopy={onCopyUserId}
                        onError={onCopyUserIdError}
                        classNames={{ value: styles.userIdValue }}
                    />
                </span>
            ) : null}
        </div>
    );
}

export default UserHeaderMeta;
